import { Background, useStore, type BackgroundVariant } from '@xyflow/react'

/**
 * The board's grid (ProjectCanvas, view.grid)
 * -------------------------------------------------------------
 * Dots, or ruled lines in two weights: fine ones every GRID_FINE, coarse ones
 * every fifth. Twice the spacing it first had (Huanrui, 2026-10-26: 22 / 110
 * -> 44 / 220), and the dots twice the size.
 *
 * The fine lines come and go with the zoom (2026-10-26), as in a drawing
 * program: zoomed out, where they would crowd into a grey wash, only the
 * coarse lines show; zoomed in on detail, the fine ones fade in between them.
 * They fade over a range of on-screen spacing (FADE_FROM to FADE_TO pixels),
 * so there is no step.
 *
 * Its own component, reading the zoom from React Flow's store in steps of a
 * tenth of the fade: panning and zooming redraw this and nothing else, and
 * only when the step changes -- not the whole board on every frame.
 */

/** The spacing of the dots and of the fine lines, in board units. */
export const GRID_FINE = 44
/** The fine lines start to show at this many pixels apart on screen... */
const FADE_FROM = 14
/** ...and are fully there at this many. */
const FADE_TO = 30

export function BoardGrid({ kind }: { kind: 'dots' | 'lines' }) {
  const fine = useStore((state) => {
    const apart = GRID_FINE * state.transform[2]
    const share = (apart - FADE_FROM) / (FADE_TO - FADE_FROM)
    return Math.round(Math.min(1, Math.max(0, share)) * 10) / 10
  })

  if (kind === 'dots') return <Background gap={GRID_FINE} size={2} variant={'dots' as BackgroundVariant} />

  /* Ruled rather than dotted, and deliberately faint: it is there to be
     lined up against, not to be looked at. Two gaps, so the coarse lines give
     something to aim at while the fine ones keep the surface from reading as
     empty. */
  return (
    <>
      {fine > 0 ? (
        <Background
          id="fine"
          gap={GRID_FINE}
          variant={'lines' as BackgroundVariant}
          lineWidth={0.5}
          color="var(--board-line)"
          style={{ opacity: fine }}
        />
      ) : null}
      <Background
        id="coarse"
        gap={GRID_FINE * 5}
        variant={'lines' as BackgroundVariant}
        lineWidth={0.8}
        color="var(--board-line-strong)"
      />
    </>
  )
}
